/**
 * V2 Google-reviews import — parsing + resolution (wave 2, step 5).
 *
 * Takeout's "Maps (your places)" exports your reviews as a GeoJSON-ish
 * file whose property names have drifted across Takeout generations
 * ("Star Rating" vs five_star_rating_published, Location.Business Name
 * vs location.name…). reviewRows() reads both by alias so a format
 * shuffle degrades to "not a reviews file", never to garbage rows.
 *
 * Resolution mirrors the lists importer (takeout.js): the row's Maps URL
 * names the exact place; a miss falls back to a Text Search on the
 * business name ANCHORED at the review's own coordinates (reviews carry
 * them — saved lists don't). Same statuses, same pacing constants.
 *
 * Star ratings are parsed but NEVER imported (design decision 2026-08-04:
 * Yoink is a journal, not a critic site — the words are the keepsake);
 * they're shown on the review screen purely to help someone recognize
 * which review is which.
 */
import { mapsLinkCandidates, textSearchPlaces, matchesName } from '../log/places.js';
import { PACING, sleep } from '../lists/takeout.js';

export const MAX_REVIEW_ROWS = 500; // same slice rule as the lists importer

/** The reviews file as rows to import; [] for anything that isn't one. */
export function reviewRows(text) {
  let data;
  try {
    data = JSON.parse(text || '');
  } catch {
    return [];
  }
  const features = Array.isArray(data?.features) ? data.features : [];
  return features
    .map((f) => {
      const p = f?.properties || {};
      const loc = p.Location || p.location || {};
      const coords = Array.isArray(f?.geometry?.coordinates) ? f.geometry.coordinates : null;
      const rating = p['Star Rating'] ?? p.five_star_rating_published ?? p.rating ?? null;
      return {
        name: String(loc['Business Name'] || loc.name || '').trim(),
        address: String(loc.Address || loc.address || '').trim(),
        url: String(p['Google Maps URL'] || p.google_maps_url || '').trim(),
        date: String(p.Published || p.date || p.published || '').trim(),
        rating: rating == null ? null : (Number(rating) || null),
        text: String(p['Review Comment'] || p.review_text_published || p.Text || p.text || '').trim(),
        lng: coords ? (coords[0] ?? null) : null,
        lat: coords ? (coords[1] ?? null) : null,
      };
    })
    .filter((r) => r.name || r.url);
}

/**
 * One row → { status, place? } — statuses match the lists importer:
 * matched / uncertain / closed / unmatched / rejected.
 */
export async function resolveReviewRow(row) {
  let candidates = [];
  const url = (row.url || '').replace(/^http:\/\//i, 'https://');
  if (url) {
    const viaLink = await mapsLinkCandidates(url).catch(() => null);
    if (viaLink?.rejected) return { status: 'rejected' };
    candidates = viaLink?.candidates || [];
  }
  if (!candidates.length && row.name) {
    candidates = await textSearchPlaces(row.name, row.lat, row.lng, 8).catch(() => []);
  }
  const best = candidates[0];
  if (!best?.placeId) return { status: 'unmatched' };
  const confident = !row.name || matchesName(row.name, best.mainText);
  const closed = (best.businessStatus || best.business_status) === 'CLOSED_PERMANENTLY';
  return {
    status: closed ? 'closed' : (confident ? 'matched' : 'uncertain'),
    place: { placeId: best.placeId, mainText: best.mainText, secondaryText: best.secondaryText || '' },
  };
}

/** Paced walk over the rows — same contract as resolveTakeoutRows. */
export async function resolveReviewRows(rows, { onRow, shouldStop } = {}) {
  const out = [];
  for (let i = 0; i < rows.length; i++) {
    if (shouldStop?.()) break;
    if (i && PACING.delayMs) await sleep(PACING.delayMs);
    if (shouldStop?.()) break;
    const resolved = { ...rows[i], ...(await resolveReviewRow(rows[i])) };
    out.push(resolved);
    onRow?.(resolved, i, rows.length);
  }
  return out;
}
